import React from 'react'
import { createPortal } from 'react-dom'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  PaperClipIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Text } from '../ui'
import { supportCenterAppointmentAttachmentsService } from '../../services/supportCenterAppointmentAttachments'
import type { SupportCenterAppointmentAttachment } from '../../types/supportCenterAppointments'
import {
  SUPPORT_CENTER_ATTACHMENT_ACCEPT,
  SUPPORT_CENTER_ATTACHMENT_MAX_MB,
} from '../../types/supportCenterAppointments'

type AppointmentAttachmentsListProps = {
  appointmentId: string | null | undefined
  /** Título da seção (padrão: Anexos). */
  title?: string
  /** Classe do container externo. */
  className?: string
  /** compact: painel lateral; section: modal com heading. */
  variant?: 'compact' | 'section'
  /** Permite anexar e remover arquivos imediatamente. */
  canUpload?: boolean
}

/**
 * Lista anexos do agendamento (triagem + atendimento) e permite abri-los via URL assinada.
 * Com `canUpload`, também permite anexar e remover (exclusão lógica) arquivos do mesmo appointment_id.
 */
export const AppointmentAttachmentsList: React.FC<AppointmentAttachmentsListProps> = ({
  appointmentId,
  title = 'Anexos',
  className,
  variant = 'compact',
  canUpload = false,
}) => {
  const [rows, setRows] = React.useState<SupportCenterAppointmentAttachment[]>([])
  const [loading, setLoading] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)
  const [removingId, setRemovingId] = React.useState<string | null>(null)
  const [pendingRemove, setPendingRemove] =
    React.useState<SupportCenterAppointmentAttachment | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [openingId, setOpeningId] = React.useState<string | null>(null)
  const [refreshKey, setRefreshKey] = React.useState(0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!appointmentId?.trim()) {
      setRows([])
      setError(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void supportCenterAppointmentAttachmentsService
      .listByAppointment(appointmentId)
      .then((data) => {
        if (!cancelled) setRows(data)
      })
      .catch((e) => {
        if (!cancelled) {
          setRows([])
          setError(e instanceof Error ? e.message : 'Não foi possível carregar os anexos.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [appointmentId, refreshKey])

  const openAttachment = React.useCallback(async (attachment: SupportCenterAppointmentAttachment) => {
    setOpeningId(attachment.id)
    setError(null)
    try {
      const url = await supportCenterAppointmentAttachmentsService.createSignedUrl(
        attachment.file_path
      )
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Não foi possível abrir o anexo.')
    } finally {
      setOpeningId(null)
    }
  }, [])

  const handleFilesSelected = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? [])
      e.target.value = ''
      if (!picked.length || !appointmentId?.trim() || uploading || removingId) return
      setUploading(true)
      setError(null)
      try {
        await supportCenterAppointmentAttachmentsService.upload(appointmentId, picked)
        setRefreshKey((key) => key + 1)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Não foi possível enviar os anexos.')
      } finally {
        setUploading(false)
      }
    },
    [appointmentId, uploading, removingId]
  )

  const requestRemove = React.useCallback(
    (attachment: SupportCenterAppointmentAttachment) => {
      if (!canUpload || uploading || removingId) return
      setError(null)
      setPendingRemove(attachment)
    },
    [canUpload, uploading, removingId]
  )

  const confirmRemove = React.useCallback(async () => {
    if (!pendingRemove || uploading || removingId) return
    const attachment = pendingRemove
    setRemovingId(attachment.id)
    setError(null)
    try {
      await supportCenterAppointmentAttachmentsService.remove(attachment)
      setPendingRemove(null)
      setRefreshKey((key) => key + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível remover o anexo.')
    } finally {
      setRemovingId(null)
    }
  }, [pendingRemove, uploading, removingId])

  if (!appointmentId?.trim()) return null

  const titleNode =
    variant === 'section' ? (
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
    ) : (
      <Text size="xs" color="muted">
        {title}
      </Text>
    )

  const emptyLabel = canUpload && !uploading ? 'Nenhum anexo ainda.' : 'Nenhum anexo.'
  const busy = uploading || !!removingId

  return (
    <div className={className ?? (variant === 'section' ? 'space-y-3' : undefined)}>
      {titleNode}
      {canUpload ? (
        <div className="space-y-2">
          <Text size="xs" color="muted">
            DOC, DOCX, PDF ou imagens — até {SUPPORT_CENTER_ATTACHMENT_MAX_MB} MB cada.
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={SUPPORT_CENTER_ATTACHMENT_ACCEPT}
            className="hidden"
            onChange={(ev) => void handleFilesSelected(ev)}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy || loading}
            className={variant === 'section' ? 'w-full sm:w-auto' : 'w-full'}
          >
            {uploading ? (
              <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
            ) : (
              <PaperClipIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            )}
            {uploading ? 'Enviando...' : 'Selecionar arquivos'}
          </Button>
        </div>
      ) : null}
      {loading ? (
        <Text size="xs" color="muted" className={variant === 'compact' ? 'mt-1' : undefined}>
          Carregando anexos...
        </Text>
      ) : rows.length === 0 && !error ? (
        <Text size="xs" color="muted" className={variant === 'compact' ? 'mt-1' : undefined}>
          {emptyLabel}
        </Text>
      ) : (
        <ul
          className={
            variant === 'compact'
              ? 'mt-1 space-y-1'
              : 'space-y-1.5 rounded-lg border border-gray-200 dark:border-gray-700 p-3'
          }
        >
          {rows.map((att) => (
            <li key={att.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void openAttachment(att)}
                disabled={openingId === att.id || busy}
                className="inline-flex min-w-0 flex-1 items-center gap-1.5 text-left text-sm text-primary-600 hover:underline dark:text-primary-400 disabled:opacity-60"
                title={`Abrir ${att.file_name}`}
              >
                {openingId === att.id ? (
                  <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
                ) : (
                  <ArrowDownTrayIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                )}
                <span className="truncate">{att.file_name}</span>
              </button>
              {canUpload ? (
                <button
                  type="button"
                  onClick={() => requestRemove(att)}
                  disabled={busy || loading}
                  className="inline-flex shrink-0 items-center justify-center rounded p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-60"
                  title={`Remover ${att.file_name}`}
                  aria-label={`Remover ${att.file_name}`}
                >
                  {removingId === att.id ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {error ? (
        <div className="mt-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      ) : null}

      {pendingRemove &&
        createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
              <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                Remover anexo
              </Heading>
              <Text size="sm" color="muted">
                Deseja remover o anexo &quot;{pendingRemove.file_name}&quot;? O arquivo deixará de
                aparecer na lista (exclusão lógica).
              </Text>
              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setPendingRemove(null)}
                  disabled={!!removingId}
                >
                  Cancelar
                </Button>
                <Button
                  variant="solid"
                  onClick={() => void confirmRemove()}
                  disabled={!!removingId}
                  className="!bg-red-600 hover:!bg-red-700 dark:!bg-red-700 dark:hover:!bg-red-600"
                >
                  {removingId ? 'Removendo...' : 'Confirmar remoção'}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
